import { useCallback, useEffect, useState } from "react";
import { getApiService } from "../services/api";
import type { ApiJob } from "../services/api";
import { InfiniteScroll } from "../components/InfiniteScroll";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { JobDialog } from "../components/JobDialog";

const PAGE_SIZE = 10;

// 任务列表项：名称、状态、Cron、模块、最近结果、操作按钮
function TaskItem({
  job,
  onEdit,
  onDelete,
  onToggle,
}: {
  job: ApiJob;
  onEdit: (j: ApiJob) => void;
  onDelete: (j: ApiJob) => void;
  onToggle: (j: ApiJob) => void;
}) {
  const moduleName = Object.keys(job.task ?? {})[0] ?? "--";
  const running = job.status === "running";
  return (
    <div className="item-card mb-3">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold">{job.name || "未命名任务"}</span>
          <span className={`badge ${running ? "badge-running" : "badge-stopped"}`}>
            {running ? "运行中" : "已停止"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn btn-sm" onClick={() => onToggle(job)}>
            {running ? "停止" : "启动"}
          </button>
          <button className="btn btn-sm" onClick={() => onEdit(job)}>
            编辑
          </button>
          <button className="btn btn-sm btn-danger" onClick={() => onDelete(job)}>
            删除
          </button>
        </div>
      </div>

      <div className="text-13px leading-6">
        <div className="flex gap-3 flat">
          <span>
            Cron：<b>{job.cron}</b>
          </span>
          <span>
            模块：<b>{moduleName}</b>
          </span>
        </div>
        <div className="flat">
          最近结果：
          <span className="break-all">{job.result || "暂无执行记录"}</span>
        </div>
      </div>
    </div>
  );
}

// 任务 Tab
export function TasksTab() {
  const api = getApiService();
  const [jobs, setJobs] = useState<ApiJob[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<ApiJob | null>(null);
  const [deleteJob, setDeleteJob] = useState<ApiJob | null>(null);
  const [toast, setToast] = useState("");

  const loadPage = useCallback(
    async (p: number, replace = false) => {
      setLoading(true);
      try {
        const res = await api.getJobList(p, PAGE_SIZE);
        const list = res.data?.jobs ?? [];
        // 空页兜底：追加页没有数据时立即停止后续加载
        if (!replace && list.length === 0) {
          setTotal(0);
          return;
        }
        setTotal(res.data?.pagination?.total ?? 0);
        setJobs((prev) => (replace ? list : [...prev, ...list]));
        setPage(p); // 记录已加载页码，避免重复请求同一页
      } catch {
        // 401 等错误由 AuthContext 统一处理
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    loadPage(1, true);
  }, [loadPage]);

  const flashToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2000);
  };

  const saveJob = async (data: {
    name: string;
    cron: string;
    task: { [key: string]: string[] };
    status?: "running" | "stopped";
  }) => {
    if (editingJob) {
      const res = await api.updateJob({
        id: editingJob.id,
        name: data.name,
        cron: data.cron,
        task: data.task,
        status: editingJob.status,
      });
      if (res.code === 200) flashToast("任务已更新");
      else flashToast(res.message ?? "更新失败");
    } else {
      const res = await api.createJob({
        name: data.name,
        cron: data.cron,
        task: data.task,
        status: data.status ?? "running",
      });
      if (res.code === 200) flashToast("任务已创建");
      else flashToast(res.message ?? "创建失败");
    }
    setEditingJob(null);
    loadPage(1, true);
  };

  const toggleJob = async (job: ApiJob) => {
    const action = job.status === "running" ? "stop" : "start";
    const res = await api.controlJob({ id: job.id, action });
    if (res.code === 200) flashToast(action === "stop" ? "任务已停止" : "任务已启动");
    else flashToast(res.message ?? "操作失败");
    loadPage(1, true);
  };

  const confirmDelete = async () => {
    if (!deleteJob) return;
    const res = await api.deleteJob(deleteJob.id);
    if (res.code === 200) flashToast("任务已删除");
    else flashToast(res.message ?? "删除失败");
    setDeleteJob(null);
    loadPage(1, true);
  };

  return (
    <div className="pt-2">
      <div className="flex justify-end mb-4">
        <button className="btn btn-primary" onClick={() => { setEditingJob(null); setJobDialogOpen(true); }}>
          + 新建任务
        </button>
      </div>

      {jobs.length === 0 && !loading ? (
        <div className="flat text-center py-10">暂无任务，点击上方按钮新建</div>
      ) : (
        <InfiniteScroll hasMore={jobs.length < total} loading={loading} onLoadMore={() => loadPage(page + 1)}>
          {jobs.map((j) => (
            <TaskItem
              key={j.id}
              job={j}
              onEdit={(job) => { setEditingJob(job); setJobDialogOpen(true); }}
              onDelete={setDeleteJob}
              onToggle={toggleJob}
            />
          ))}
        </InfiniteScroll>
      )}

      <JobDialog
        open={jobDialogOpen}
        job={editingJob}
        onClose={() => setJobDialogOpen(false)}
        onSave={saveJob}
      />

      <ConfirmDialog
        open={!!deleteJob}
        title="确认删除"
        message={`确定删除任务「${deleteJob?.name ?? ""}」吗？`}
        confirmText="删除"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteJob(null)}
      />

      {toast && (
        <div
          className="fixed left-1/2 -translate-x-1/2 bottom-8 px-4 py-2 rounded-full text-13px"
          style={{ background: "var(--chart-tooltip)", color: "var(--chart-tooltip-fg)", zIndex: 200 }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}
